import { Alert, Box, Card, Select, Text, Title, useToast } from '@nimbus-ds/components';
import { FormField, Page } from '@nimbus-ds/patterns';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAnalyticsPanel, type AnalyticsPanel } from '../api/client.js';
import { TrendChart } from '../components/TrendChart.js';
import { AnalyticsPanelSkeleton } from '../components/skeletons.js';
import { hasOfferActivity } from './analytics.model.js';

type Preset = 'last7' | 'last30' | 'last90' | 'thisMonth' | 'custom';

const PRESETS: Preset[] = ['last7', 'last30', 'last90', 'thisMonth', 'custom'];

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftDays(day: string, delta: number): string {
  const d = new Date(`${day}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/** Resolve a preset (or the custom inputs) to an inclusive `{ from, to }` UTC day pair. */
function resolveRange(
  preset: Preset,
  customFrom: string,
  customTo: string,
): { from: string; to: string } {
  const to = todayUtc();
  switch (preset) {
    case 'last7':
      return { from: shiftDays(to, -6), to };
    case 'last30':
      return { from: shiftDays(to, -29), to };
    case 'last90':
      return { from: shiftDays(to, -89), to };
    case 'thisMonth':
      return { from: `${to.slice(0, 7)}-01`, to };
    case 'custom':
      return { from: customFrom, to: customTo };
  }
}

/** Format money with the store currency; fall back to a plain 2-decimal number. */
function formatMoney(value: number, currency: string | null, locale: string): string {
  if (currency) {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
    } catch {
      /* invalid currency code — fall through to plain formatting */
    }
  }
  return value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Renders a stat value on a single line, shrinking the font just enough to fit the card.
 * Large-currency totals (e.g. COP/CLP revenue) stay fully precise instead of overflowing
 * past the card's edge or wrapping mid-number.
 */
function FitValue({ as, children }: { as: 'h3' | 'h4'; children: string }): JSX.Element {
  const boxRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const el = box?.firstElementChild as HTMLElement | null;
    if (!box || !el) return;

    let lastWidth = -1;
    const fit = (): void => {
      const avail = box.clientWidth;
      // Only react to width changes; shrinking the font also changes height, and reacting
      // to that would loop the ResizeObserver.
      if (avail === lastWidth) return;
      lastWidth = avail;
      el.style.fontSize = ''; // revert to the Nimbus token size before measuring
      const base = parseFloat(getComputedStyle(el).fontSize) || 24;
      const natural = el.scrollWidth;
      if (avail > 0 && natural > avail) {
        el.style.fontSize = `${Math.max(12, (base * (avail - 2)) / natural)}px`;
      }
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    // Web fonts can settle after first paint and shift text metrics — re-fit once ready.
    void document.fonts?.ready.then(() => {
      lastWidth = -1;
      fit();
    });
    return () => ro.disconnect();
  }, [children, as]);

  return (
    <div ref={boxRef} style={{ overflow: 'hidden' }}>
      <Title as={as} style={{ whiteSpace: 'nowrap' }}>
        {children}
      </Title>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Box flex="1 1 160px" minWidth="150px">
      <Card>
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="1">
            <Text fontSize="caption" color="neutral-textLow">
              {label}
            </Text>
            <FitValue as="h3">{value}</FitValue>
          </Box>
        </Card.Body>
      </Card>
    </Box>
  );
}

function TrendCard({
  title,
  value,
  data,
  labels,
  color,
  emptyLabel,
  formatValue,
}: {
  title: string;
  value: string;
  data: number[];
  labels: string[];
  color: string;
  emptyLabel: string;
  /** A single day's value as the hover tooltip should say it — the unit lives here, not in the
   *  chart, which plots plain numbers and cannot know whether one of them is an order or a peso. */
  formatValue: (value: number) => string;
}): JSX.Element {
  return (
    <Box flex="1 1 260px" minWidth="240px">
      <Card>
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="2">
            <Box display="flex" flexDirection="column" gap="1">
              <Text fontSize="caption" color="neutral-textLow">
                {title}
              </Text>
              <FitValue as="h4">{value}</FitValue>
            </Box>
            <TrendChart
              data={data}
              labels={labels}
              color={color}
              emptyLabel={emptyLabel}
              title={title}
              formatValue={formatValue}
            />
          </Box>
        </Card.Body>
      </Card>
    </Box>
  );
}

export function Analytics(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();

  const [preset, setPreset] = useState<Preset>('last30');
  const [customFrom, setCustomFrom] = useState<string>(() => shiftDays(todayUtc(), -29));
  const [customTo, setCustomTo] = useState<string>(() => todayUtc());
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<AnalyticsPanel | null>(null);

  const range = useMemo(
    () => resolveRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  // Custom range needs both ends and from <= to before we query.
  const rangeValid = Boolean(range.from) && Boolean(range.to) && range.from <= range.to;

  useEffect(() => {
    if (!rangeValid) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const data = await getAnalyticsPanel(range.from, range.to);
        if (!cancelled) setPanel(data);
      } catch {
        if (!cancelled) {
          addToast({ id: 'analytics-load', type: 'danger', text: t('common.saveError') });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to, rangeValid, addToast, t]);

  const locale = i18n.language;
  const currency = panel?.currency ?? null;
  const c = panel?.cards;
  const series = panel?.timeseries ?? [];
  const labels = series.map((p) => p.date);

  return (
    <Page maxWidth="1200px">
      <Page.Body>
        <Box display="flex" flexDirection="column" gap="4">
          {/* Date-range selector */}
          <Card>
            <Card.Body>
              <Box display="flex" flexWrap="wrap" gap="4" alignItems="flex-end">
                <Box
                  flex="1 1 220px"
                  minWidth="200px"
                  display="flex"
                  flexDirection="column"
                  gap="1"
                >
                  <Text>{t('analytics.dateRange')}</Text>
                  <Select
                    id="analytics-range"
                    name="analytics-range"
                    value={preset}
                    onChange={(e) => setPreset((e.target as HTMLSelectElement).value as Preset)}
                  >
                    {PRESETS.map((p) => (
                      <Select.Option key={p} value={p} label={t(`analytics.presets.${p}`)} />
                    ))}
                  </Select>
                </Box>
                {preset === 'custom' && (
                  <>
                    <Box flex="1 1 160px" minWidth="150px">
                      <FormField.Input
                        id="analytics-from"
                        type="date"
                        label={t('analytics.from')}
                        value={customFrom}
                        onChange={(e) => setCustomFrom((e.target as HTMLInputElement).value)}
                      />
                    </Box>
                    <Box flex="1 1 160px" minWidth="150px">
                      <FormField.Input
                        id="analytics-to"
                        type="date"
                        label={t('analytics.to')}
                        value={customTo}
                        onChange={(e) => setCustomTo((e.target as HTMLInputElement).value)}
                      />
                    </Box>
                  </>
                )}
              </Box>
              {!rangeValid && (
                <Box marginTop="2">
                  <Text fontSize="caption" color="danger-textLow">
                    {t('analytics.invalidRange')}
                  </Text>
                </Box>
              )}
            </Card.Body>
          </Card>

          {/* Lifetime revenue celebration banner */}
          {panel && panel.lifetimeRevenue > 0 && (
            <Alert appearance="success" title={t('analytics.lifetimeTitle')}>
              {t('analytics.lifetimeBody', {
                amount: formatMoney(panel.lifetimeRevenue, currency, locale),
              })}
            </Alert>
          )}

          {loading || !panel || !c ? (
            // Skeleton, not a spinner: this area is content, and reserving its height stops the
            // page jumping when the panel lands. (Checklist: prefer Skeleton for content areas.)
            // It draws the panel's real tree — five metric cards, three trend cards, the offers
            // card — because reserving the wrong height is its own kind of jump.
            <AnalyticsPanelSkeleton />
          ) : (
            <>
              {/* Metric cards */}
              <Box display="flex" flexWrap="wrap" gap="4">
                <StatCard label={t('analytics.formOpens')} value={c.opens.toLocaleString(locale)} />
                <StatCard label={t('analytics.orders')} value={c.orders.toLocaleString(locale)} />
                <StatCard
                  label={t('analytics.conversionRate')}
                  value={`${(c.conversionRate * 100).toFixed(1)}%`}
                />
                <StatCard
                  label={t('analytics.revenue')}
                  value={formatMoney(c.revenue, currency, locale)}
                />
                <StatCard label={t('analytics.aov')} value={formatMoney(c.aov, currency, locale)} />
              </Box>

              {/* Trends */}
              <Box display="flex" flexWrap="wrap" gap="4">
                <TrendCard
                  title={t('analytics.trendOpens')}
                  value={c.opens.toLocaleString(locale)}
                  data={series.map((p) => p.opens)}
                  labels={labels}
                  color="#2563EB"
                  emptyLabel={t('analytics.noData')}
                  formatValue={(v) =>
                    t('analytics.tooltipOpens', { count: v, value: v.toLocaleString(locale) })
                  }
                />
                <TrendCard
                  title={t('analytics.trendOrders')}
                  value={c.orders.toLocaleString(locale)}
                  data={series.map((p) => p.orders)}
                  labels={labels}
                  color="#16A34A"
                  emptyLabel={t('analytics.noData')}
                  formatValue={(v) =>
                    t('analytics.tooltipOrders', { count: v, value: v.toLocaleString(locale) })
                  }
                />
                <TrendCard
                  title={t('analytics.trendRevenue')}
                  value={formatMoney(c.revenue, currency, locale)}
                  data={series.map((p) => p.revenue)}
                  labels={labels}
                  color="#9333EA"
                  emptyLabel={t('analytics.noData')}
                  formatValue={(v) => formatMoney(v, currency, locale)}
                />
              </Box>

              {/* Quantity Offers contribution */}
              <Card>
                <Card.Header title={t('analytics.quantityOffers')} />
                <Card.Body>
                  <Box display="flex" flexDirection="column" gap="3">
                    <Box display="flex" flexWrap="wrap" gap="4">
                      <StatCard
                        label={t('analytics.qoSelections')}
                        value={panel.quantityOffers.selections.toLocaleString(locale)}
                      />
                      <StatCard
                        label={t('analytics.qoRevenue')}
                        value={formatMoney(
                          panel.quantityOffers.revenueContribution,
                          currency,
                          locale,
                        )}
                      />
                      <StatCard
                        label={t('analytics.qoDiscount')}
                        value={formatMoney(panel.quantityOffers.discountGiven, currency, locale)}
                      />
                    </Box>
                    {/* The note explains why three zeros are not a bug: nothing has been through
                        a quantity offer yet in this range. Once the numbers are real it explains
                        nothing and is just a line of grey text under them, so it goes away. */}
                    {!hasOfferActivity(panel.quantityOffers) && (
                      <Text fontSize="caption" color="neutral-textLow">
                        {t('analytics.qoNote')}
                      </Text>
                    )}
                  </Box>
                </Card.Body>
              </Card>
            </>
          )}
        </Box>
      </Page.Body>
    </Page>
  );
}
